// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Star Systems Configuration - Universe Discworld
 * 
 * A universe where magic works, turtles swim through space, and narrative causality
 * is a fundamental force. Based on Terry Pratchett's Discworld and Science of Discworld.
 * Where L-Space connects all libraries and the Librarian goes "Ook!"
 */

export const starSystems = [
  {
    id: 500,
    name: 'DISCWORLD',
    distance: 0, // Starting system
    threat: 'NARRATIVE CAUSALITY',
    resources: 'MAGIC & BELIEF',
    services: ['refuel', 'trade', 'weapons', 'exploration'],
    description: 'A flat world carried by four elephants on the back of Great A\'Tuin. Magic is real here.',
    primaryColor: '#8B4513',
    secondaryColor: '#228B22',
    hasRings: false,
    animated: true,
  },
  {
    id: 501,
    name: 'ROUNDWORLD',
    distance: 50,
    threat: 'RULES',
    resources: 'SCIENCE & LOGIC',
    services: ['refuel', 'trade', 'weapons', 'upgrades'],
    description: 'A spherical world where magic doesn\'t work and everything follows boring rules. Also called Earth.',
    primaryColor: '#4169E1',
    secondaryColor: '#228B22',
    hasRings: false,
    animated: true,
  },
  {
    id: 502,
    name: 'XXXX',
    distance: 100,
    threat: 'EXTREMELY DANGEROUS',
    resources: 'BEER & PRAWNS',
    services: ['refuel', 'trade', 'exploration'],
    description: 'The last continent. Everything here wants to kill you, especially the sheep. Excellent beer though.',
    primaryColor: '#FF4500',
    secondaryColor: '#FFD700',
    hasRings: false,
    animated: true,
  },
  {
    id: 503,
    name: 'KLATCH',
    distance: 75,
    threat: 'DIPLOMATIC',
    resources: 'SPICES & CARPETS',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Desert continent known for flying carpets, djinn, and the finest coffee in the multiverse.',
    primaryColor: '#DAA520',
    secondaryColor: '#8B4513',
    hasRings: false,
    animated: true,
  },
  {
    id: 504,
    name: 'FOURECKS MOON',
    distance: 120,
    threat: 'WEREWOLVES',
    resources: 'MOONLIGHT & SILVER',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Moon of XXXX. Home to werewolves and other creatures that go bump in the night.',
    primaryColor: '#C0C0C0',
    secondaryColor: '#4682B4',
    hasRings: false,
    animated: true,
  },
  {
    id: 505,
    name: 'LLAMEDOS',
    distance: 90,
    threat: 'SINGING',
    resources: 'COAL & SHEEP',
    services: ['refuel', 'trade', 'exploration'],
    description: 'A land of valleys, coal mines, and people who communicate primarily through song.',
    primaryColor: '#2F4F4F',
    secondaryColor: '#228B22',
    hasRings: false,
    animated: false,
  },
  {
    id: 510,
    name: 'LANCRE',
    distance: 95,
    threat: 'WITCHES',
    resources: 'HERBS & HEADOLOGY',
    services: ['refuel', 'trade', 'exploration'],
    description: 'Small mountain kingdom ruled by witches. Granny Weatherwax knows you\'re coming.',
    primaryColor: '#228B22',
    secondaryColor: '#8B4513',
    hasRings: false,
    animated: true,
  },
  {
    id: 511,
    name: 'RAMTOPS',
    distance: 105,
    threat: 'MOUNTAIN MAGIC',
    resources: 'CRYSTAL & STONE',
    services: ['refuel', 'trade', 'weapons'],
    description: 'High mountain range where magic runs wild and the air is thin. Home to many witches and wizards.',
    primaryColor: '#708090',
    secondaryColor: '#F0F8FF',
    hasRings: false,
    animated: true,
  },
  {
    id: 512,
    name: 'BAD ASS',
    distance: 98,
    threat: 'GRANNY WEATHERWAX',
    resources: 'HEADOLOGY',
    services: ['refuel', 'trade'],
    description: 'Granny Weatherwax\'s village. She\'s probably already fixed whatever you came here for.',
    primaryColor: '#8B4513',
    secondaryColor: '#228B22',
    hasRings: false,
    animated: true,
  },
  {
    id: 506,
    name: 'ÜBERWALD',
    distance: 140,
    threat: 'UNDEAD',
    resources: 'DARK MAGIC',
    services: ['refuel', 'trade', 'weapons', 'exploration'],
    description: 'Land of vampires, werewolves, and Igor. Excellent for those who prefer the night shift.',
    primaryColor: '#8B0000',
    secondaryColor: '#2F2F2F',
    hasRings: false,
    animated: true,
  },
  {
    id: 513,
    name: 'GENUA',
    distance: 130,
    threat: 'FAIRY GODMOTHERS',
    resources: 'STORIES & SWAMPS',
    services: ['refuel', 'trade', 'weapons', 'exploration'],
    description: 'City of stories and fairy tales. Beware of helpful fairy godmothers with their own agendas.',
    primaryColor: '#FF69B4',
    secondaryColor: '#32CD32',
    hasRings: false,
    animated: true,
  },
  {
    id: 514,
    name: 'EPHEBE',
    distance: 125,
    threat: 'PHILOSOPHY',
    resources: 'WISDOM & OLIVES',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Ancient city-state of philosophers. They\'ll debate the existence of your ship while you\'re in it.',
    primaryColor: '#F0E68C',
    secondaryColor: '#8FBC8F',
    hasRings: false,
    animated: true,
  },
  {
    id: 515,
    name: 'OMNIA',
    distance: 135,
    threat: 'RELIGIOUS FERVOR',
    resources: 'FAITH & TORTOISES',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Theocratic state. The Great God Om may or may not be a small tortoise.',
    primaryColor: '#DAA520',
    secondaryColor: '#8B4513',
    hasRings: false,
    animated: true,
  },
  {
    id: 516,
    name: 'TSORT',
    distance: 150,
    threat: 'ANCIENT GRUDGES',
    resources: 'ARCHAEOLOGY',
    services: ['refuel', 'trade', 'exploration'],
    description: 'Ancient city with a very long memory. Still arguing about that horse incident.',
    primaryColor: '#CD853F',
    secondaryColor: '#F4A460',
    hasRings: false,
    animated: false,
  },
  {
    id: 517,
    name: 'DJELIBEYBI',
    distance: 155,
    threat: 'PYRAMIDS',
    resources: 'ANCIENT MAGIC',
    services: ['refuel', 'trade', 'weapons', 'exploration'],
    description: 'The Old Kingdom. Pyramids everywhere, and time runs a bit differently here.',
    primaryColor: '#DAA520',
    secondaryColor: '#8B4513',
    hasRings: false,
    animated: true,
  },
  {
    id: 507,
    name: 'COUNTERWEIGHT CONTINENT',
    distance: 180,
    threat: 'ANCIENT WISDOM',
    resources: 'TEA & PHILOSOPHY',
    services: ['refuel', 'trade', 'weapons', 'upgrades'],
    description: 'The mysterious eastern continent. Home to ancient wisdom, martial arts, and really good tea.',
    primaryColor: '#FF6347',
    secondaryColor: '#FFD700',
    hasRings: false,
    animated: true,
  },
  {
    id: 518,
    name: 'HUNGHUNG',
    distance: 175,
    threat: 'IMPERIAL BUREAUCRACY',
    resources: 'SILK & PORCELAIN',
    services: ['refuel', 'trade', 'weapons', 'upgrades'],
    description: 'Capital of the Agatean Empire. The bureaucracy is so complex it\'s become sentient.',
    primaryColor: '#FF6347',
    secondaryColor: '#FFD700',
    hasRings: false,
    animated: true,
  },
  {
    id: 519,
    name: 'BES PELARGIC',
    distance: 185,
    threat: 'TRADE WARS',
    resources: 'EXOTIC GOODS',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Major trading port of the Counterweight Continent. Everything has a price here.',
    primaryColor: '#FF4500',
    secondaryColor: '#FFD700',
    hasRings: false,
    animated: true,
  },
  {
    id: 520,
    name: 'QUIRM',
    distance: 115,
    threat: 'CUISINE',
    resources: 'FINE FOOD',
    services: ['refuel', 'trade', 'exploration'],
    description: 'City famous for its cuisine. The food is so good it\'s almost magical.',
    primaryColor: '#FF69B4',
    secondaryColor: '#32CD32',
    hasRings: false,
    animated: true,
  },
  {
    id: 508,
    name: 'KRULL',
    distance: 220,
    threat: 'EDGE OF WORLD',
    resources: 'RIMFALL WATER',
    services: ['refuel', 'trade', 'exploration'],
    description: 'Island nation at the very edge of the Disc. Watch out for the Circumfence and the waterfall.',
    primaryColor: '#4682B4',
    secondaryColor: '#87CEEB',
    hasRings: false,
    animated: true,
  },
  {
    id: 521,
    name: 'WYRMBERG',
    distance: 200,
    threat: 'DRAGONS',
    resources: 'DRAGON MAGIC',
    services: ['refuel', 'weapons', 'exploration'],
    description: 'Floating mountain home to the last dragons. They exist only in the imagination, but that makes them more dangerous.',
    primaryColor: '#8B0000',
    secondaryColor: '#FFD700',
    hasRings: false,
    animated: true,
  },
  {
    id: 522,
    name: 'SKUND FOREST',
    distance: 190,
    threat: 'WILD MAGIC',
    resources: 'RARE HERBS',
    services: ['refuel', 'exploration'],
    description: 'Ancient forest where magic runs wild. The trees remember when the world was young.',
    primaryColor: '#228B22',
    secondaryColor: '#8B4513',
    hasRings: false,
    animated: true,
  },
  {
    id: 509,
    name: 'DUNGEON DIMENSIONS',
    distance: 300,
    threat: 'ELDRITCH HORROR',
    resources: 'PURE CHAOS',
    services: ['refuel', 'weapons'],
    description: 'The space between spaces where Things live. Not recommended for casual tourism.',
    primaryColor: '#4B0082',
    secondaryColor: '#8B008B',
    hasRings: true,
    animated: true,
  },
];

// Space Stations in the Discworld Universe
export const spaceStations = [
  {
    id: 550,
    name: 'UNSEEN UNIVERSITY OBSERVATORY',
    distance: 25,
    threat: 'MAGICAL ACCIDENTS',
    resources: 'KNOWLEDGE & BANANAS',
    services: ['refuel', 'trade', 'weapons', 'upgrades', 'modules'],
    description: 'Floating magical observatory. The Librarian maintains the L-Space navigation systems. Ook!',
    type: 'station',
    animated: true,
  },
  {
    id: 551,
    name: 'PATRICIAN\'S ORBITAL PLATFORM',
    distance: 60,
    threat: 'BUREAUCRACY',
    resources: 'ORDER & EFFICIENCY',
    services: ['refuel', 'trade', 'weapons', 'upgrades', 'modules'],
    description: 'Lord Vetinari\'s personal space station. Everything runs with terrifying efficiency.',
    type: 'station',
    animated: true,
  },
  {
    id: 552,
    name: 'IGOR\'S SPARE PARTS DEPOT',
    distance: 110,
    threat: 'MEDICAL MALPRACTICE',
    resources: 'SPARE PARTS',
    services: ['refuel', 'trade', 'modules'],
    description: 'Igor\'s orbital workshop. "Thith will only hurt a little, marthter." Excellent for ship repairs.',
    type: 'station',
    animated: true,
  },
  {
    id: 553,
    name: 'DEATH\'S DOMAIN OUTPOST',
    distance: 250,
    threat: 'MORTALITY',
    resources: 'DUTY & CURRY',
    services: ['refuel', 'trade', 'weapons', 'upgrades'],
    description: 'Death\'s personal space station. WHAT CAN THE HARVEST HOPE FOR, IF NOT THE CARE OF THE REAPER MAN?',
    type: 'station',
    animated: true,
  },
  {
    id: 554,
    name: 'COHEN\'S BARBARIAN OUTPOST',
    distance: 160,
    threat: 'HEROIC',
    resources: 'ADVENTURE & LOOT',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Cohen the Barbarian\'s retirement station. Still looking for one last great adventure.',
    type: 'station',
    animated: true,
  },
];

export const starPositions = starSystems.reduce((acc, system) => {
  if (system.coordinates) {
    acc[system.id] = system.coordinates;
  }
  return acc;
}, {});